import { Link } from 'react-router';
import { Avatar, SeverityBadge, StateBadge } from '../../components/ui/Badge.js';
import { RelativeTime } from '../../components/ui/Time.js';
import { cx } from '../../lib/cx.js';
import type { Directory } from '../../lib/directory.js';
import { formatCount } from '../../lib/format.js';
import type { Alarm } from '../../lib/schemas.js';
import { SEVERITY_TOKENS } from '../../lib/severity.js';

export interface AlarmCardsProps {
  alarms: Alarm[];
  directory: Directory;
  selected: Set<string>;
  onToggle: (id: string, shiftKey: boolean) => void;
  highlighted?: Set<string>;
  className?: string;
}

/**
 * The alarm list on a phone.
 *
 * Eight columns in 390 px leaves about a hundred pixels for the title, which is
 * the one thing worth reading, so the table gives way to a two-line card: the
 * title on its own line, then severity, state, entity and age. Everything else
 * — source, count, assignee — is on the detail page, one tap away.
 */
export function AlarmCards({
  alarms,
  directory,
  selected,
  onToggle,
  highlighted,
  className,
}: AlarmCardsProps) {
  return (
    <ul
      className={cx(
        'overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)]',
        className,
      )}
    >
      {alarms.map((alarm) => {
        const assignee = alarm.assignee?.display_name ?? directory.userName(alarm.assignee_id);
        return (
          <li
            key={alarm.id}
            className={cx(
              'relative border-b border-[var(--border)] last:border-0',
              selected.has(alarm.id) ? 'bg-[var(--accent-soft)]' : 'bg-[var(--surface)]',
              highlighted?.has(alarm.id) && 'vam-flash',
            )}
          >
            <span
              aria-hidden
              className="absolute left-0 top-0 h-full w-[3px]"
              style={{ backgroundColor: SEVERITY_TOKENS[alarm.severity].ink }}
            />
            <div className="flex items-start gap-2.5 py-2.5 pl-3 pr-2.5">
              <input
                type="checkbox"
                checked={selected.has(alarm.id)}
                aria-label={`Select ${alarm.title}`}
                onChange={(event) =>
                  onToggle(alarm.id, (event.nativeEvent as MouseEvent).shiftKey === true)
                }
                className="mt-1 h-4 w-4 shrink-0 accent-[var(--accent)]"
              />
              <div className="min-w-0 flex-1">
                <Link
                  to={`/alarms/${alarm.id}`}
                  className="line-clamp-2 text-[var(--text-base)] font-medium text-[var(--text)]"
                >
                  {alarm.title}
                </Link>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <SeverityBadge severity={alarm.severity} />
                  <StateBadge state={alarm.state} suppressed={alarm.suppressed} />
                  {alarm.entity.name && (
                    <span className="min-w-0 truncate text-[var(--text-sm)] text-[var(--text-2)]">
                      {alarm.entity.name}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[var(--text-sm)] text-[var(--text-3)]">
                  <RelativeTime value={alarm.last_seen} withSuffix className="tnum" />
                  <span aria-hidden>·</span>
                  <span className="tnum">{formatCount(alarm.occurrence_count)}×</span>
                  {assignee && (
                    <span className="ml-auto flex items-center gap-1">
                      <Avatar name={assignee} size={16} />
                      <span className="truncate">{assignee}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
